import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@nexus/ui"
import { useTranslation } from "react-i18next"
import { truncateSetName } from "../../../lib/format-opportunity.js"

export interface DeleteSetDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  setName: string
  onConfirm: () => void
  isDeleting?: boolean
  /** When true, swaps copy to "Cancel generation" semantics instead of "Remove set". */
  isGenerating?: boolean
}

export function DeleteSetDialog({ open, onOpenChange, setName, onConfirm, isDeleting, isGenerating }: DeleteSetDialogProps) {
  const { t } = useTranslation()

  const displayName = truncateSetName(setName)

  const title = isGenerating
    ? t("opportunities.cancelGenerationTitle", "Cancel generation")
    : t("opportunities.deleteSet", "Remove set")

  const description = isGenerating
    ? t("opportunities.cancelGenerationDescription", {
        name: displayName,
        defaultValue: `Stop generating opportunities for "${displayName}"? The set will be removed.`,
      })
    : t("opportunities.deleteSetDescription", {
        name: displayName,
        defaultValue: `Are you sure you want to remove "${displayName}"? All opportunities in this set will also be removed. This action cannot be undone from the UI.`,
      })

  const confirmLabel = isGenerating
    ? (isDeleting ? t("opportunities.cancellingGeneration", "Cancelling…") : t("opportunities.stopAndRemove", "Stop & remove"))
    : (isDeleting ? t("common.removing", "Removing…") : t("common.remove", "Remove"))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={isDeleting}>
            {t("common.cancel")}
          </Button>
          <Button type="button" variant="destructive" onClick={onConfirm} disabled={isDeleting}>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
